"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import AreaChart from "@/src/components/charts/area-chart";

const cashFlowData = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  series: [
    { name: "Inflow", data: [2800, 3200, 3800, 4200, 4800, 5200, 5800, 6200, 6800, 7200, 7800, 8400] },
    { name: "Outflow", data: [1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200, 3400, 3600, 3800, 4000] },
  ],
};

const singleSeriesData = {
  categories: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6"],
  series: [
    { name: "User Growth", data: [1200, 1400, 1600, 1900, 2100, 2500], color: "#3b82f6" },
  ],
};

const stackedData = {
  categories: ["Q1", "Q2", "Q3", "Q4"],
  series: [
    { name: "Product A", data: [420, 510, 580, 680] },
    { name: "Product B", data: [310, 360, 390, 460] },
    { name: "Product C", data: [220, 280, 320, 400] },
  ],
};

export default function DocsAreaChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Area Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Visualize magnitude and trends with filled areas under lines.
              Great for showing cumulative values, cash flow, and multi-series comparisons.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Cash Flow Over 12 Months</CardTitle>}
            contentClassName="p-theme-5"
          >
            <AreaChart data={cashFlowData} height={350} gradient={true} />
          </SectionCard>
        </Section>

        {/* Variants */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Single Series</CardTitle>}
              contentClassName="p-theme-5"
            >
              <AreaChart data={singleSeriesData} height={300} smooth={true} />
            </SectionCard>

            <SectionCard
              header={<CardTitle>Stacked Areas</CardTitle>}
              contentClassName="p-theme-5"
            >
              <AreaChart data={stackedData} height={300} stacked={true} smooth={true} />
            </SectionCard>
          </div>
        </Section>

        {/* Without Gradient */}
        <Section title="Without Gradient">
          <SectionCard
            header={<CardTitle>Solid Fill Areas</CardTitle>}
            contentClassName="p-theme-5"
          >
            <AreaChart data={cashFlowData} height={350} gradient={false} />
          </SectionCard>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>AreaChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">AreaChartData</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Categories and series data</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">100%</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">smooth</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Use smooth curves instead of straight lines</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">stacked</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Stack areas on top of each other</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLegend</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show legend</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">gradient</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Use gradient fill (top to transparent)</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(value: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in tooltip</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onPointClick</td>
                  <td className="py-2 px-2">(params) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for points</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>AreaChartData Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface AreaChartData {
  categories: string[];
  series: {
    name: string;           // Series name in legend
    data: number[];         // Data points matching categories
    color?: string;         // Optional custom color (hex/CSS)
  }[];
}

// Example:
const data = {
  categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  series: [
    {
      name: "Inflow",
      data: [2800, 3200, 3800, 4200, 4800, 5200]
    },
    {
      name: "Outflow",
      data: [1800, 2000, 2200, 2400, 2600, 2800]
    }
  ]
};`}</code>
            </pre>
          </SectionCard>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsAreaChartPage as AreaChartPage };
